import {
  Autocomplete,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { DISPLAY_DATE_TOOLTIP, formatDisplayDateTime } from "utils/dateFormat";
import { DisplayDateTime } from "components/DisplayDate";
import useKothar from "context/useKothar";
import { useMemo, useState } from "react";
import { AiOutlineUserSwitch } from "react-icons/ai";
import { FaExclamationCircle, FaTrash } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useAddTaskAssignee,
  useDeleteTaskComment,
  useNotifyAdminAboutTask,
  usePostTaskComment,
  useRemoveTaskAssignee,
  useTaskById,
  useTaskComments,
  useUpdateTask,
  useChangeTaskStatus,
} from "utils/taskService";
import {
  buildActivityItems,
  defaultActivityMessage,
  extractMentionIds,
  formatRelativeTime,
  formatTaskDate,
  getDaysLeftLabel,
  getDeadlineInfo,
  getSubtaskAssignee,
  getSubtaskProgress,
  getSubtasks,
  getTaskAdminRecipients,
  getTaskAssignableUsers,
  getTaskAssignees,
  getTaskOwner,
  getTaskPriorityColor,
  getTaskStatusColor,
  normalizeTaskUser,
  renderMentionSegments,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "utils/taskUtils";
import CommentAttachments from "components/Tasks/CommentAttachments";
import TaskAttachmentList from "components/Tasks/TaskAttachmentList";
import CommentComposer from "components/Tasks/CommentComposer";
import SubtaskManager from "components/Tasks/SubtaskManager";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import NotifyAdminDialog from "components/Tasks/NotifyAdminDialog";
import TaskAssigneeAvatars from "components/Tasks/TaskAssigneeAvatars";
import ViewClientProfile from "components/Clients/ViewClientProfile";
import TaskModal from "./TaskModal";
import ViewPriceListModal from "../PriceList/ViewPriceListModal";
import ViewCheckListModal from "../CheckList/ViewCheckListModal";
import {
  getCheckListTitle,
  getPriceListTitle,
  useCheckListById,
  usePriceListById,
} from "utils/catalogService";
import {
  getModulePaymentTaskTypeLabel,
  isModulePaymentTaskType,
} from "../Accounts/modulePaymentTaskTypes";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";

const SidebarLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-wide font-semibold text-app-text-muted m-0 mb-1.5">
    {children}
  </p>
);

const DetailItem = ({ label, value, onClick, hint, hintClass = "text-app-text-muted" }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-wide text-app-text-muted m-0">
      {label}
      {hint ? <span className={`ml-1 font-semibold normal-case ${hintClass}`}>{hint}</span> : null}
    </p>
    {onClick ? (
      <button
        type="button"
        onClick={onClick}
        title={value}
        className="text-xs font-medium text-primary hover:underline bg-transparent border-0 p-0 cursor-pointer truncate max-w-[220px] text-left block"
      >
        {value}
      </button>
    ) : (
      <p className="text-xs font-medium text-app-text m-0 truncate max-w-[220px]" title={value}>
        {value}
      </p>
    )}
  </div>
);

const ViewTask = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [isPriceListModalOpen, setIsPriceListModalOpen] = useState(false);
  const [isCheckListModalOpen, setIsCheckListModalOpen] = useState(false);
  const [reassignmentData, setReassignmentData] = useState({
    assignTo: null,
    reasonOfReassignment: "",
  });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState(null);
  const [newAssignee, setNewAssignee] = useState(null);
  const [assigneePendingRemoval, setAssigneePendingRemoval] = useState(null);
  const [commentPendingDeletion, setCommentPendingDeletion] = useState(null);
  const [showNotifyAdminDialog, setShowNotifyAdminDialog] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentMentionIds, setCommentMentionIds] = useState([]);
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [ownerDraft, setOwnerDraft] = useState(null);

  const [{ usersList }] = useKothar();

  const { data: taskDetails, refetch, isFetching, isError } = useTaskById(id);

  const { data: comments = [], refetch: refetchComments } = useTaskComments(id);

  const priceListId = taskDetails?.priceListId ?? null;
  const checkListId = taskDetails?.checkListId ?? null;
  const { data: priceListDetail } = usePriceListById(priceListId);
  const { data: checkListDetail } = useCheckListById(checkListId);

  const isModulePaymentTask = isModulePaymentTaskType(taskDetails?.taskType);
  const currentUserEmail = localStorage.getItem("userEmail");
  // The backend decides both rights and ships them on the task, so this screen cannot drift
  // from the rules it enforces. canEditDetails is the assigner (or a super admin); everyone on
  // the task -- collaborators included -- may move its status.
  const canEditDetails = Boolean(taskDetails?.canEditDetails);
  const canChangeStatus = Boolean(taskDetails?.canChangeStatus);
  const canUpdateTask = hasPermission(P.TASKS.UPDATE);
  // Collaborators are their own right: the primary assignee may bring people in and drop the ones
  // they brought, without being able to redefine the task.
  const canManageCollaborators = Boolean(taskDetails?.canManageCollaborators);
  const removableCollaboratorIds = useMemo(
    () => new Set(taskDetails?.removableCollaboratorIds || []),
    [taskDetails?.removableCollaboratorIds]
  );

  const deadlineInfo = useMemo(() => getDeadlineInfo(taskDetails), [taskDetails]);
  const daysLeftLabel = getDaysLeftLabel(deadlineInfo?.days);
  const subtaskProgress = useMemo(() => getSubtaskProgress(taskDetails), [taskDetails]);
  const allAssignees = useMemo(() => getTaskAssignees(taskDetails), [taskDetails]);
  const collaborators = useMemo(
    () => allAssignees.filter((person) => person.email !== taskDetails?.assignedTo?.email),
    [allAssignees, taskDetails?.assignedTo?.email]
  );
  // The API returns the owner as { userId, name, email }; reading `.id` off it yields undefined,
  // which drops assignTo from every quick update and 500s the backend.
  const currentOwnerId = useMemo(
    () => getTaskOwner(taskDetails)?.id ?? taskDetails?.assignedToId ?? null,
    [taskDetails]
  );
  const orgUsers = useMemo(() => {
    const everyone = usersList || [];
    const taskOrgId = taskDetails?.organizationId;
    if (!taskOrgId) return everyone;
    return everyone.filter((user) => (user?.organization?.id ?? user?.orgId) === taskOrgId);
  }, [usersList, taskDetails?.organizationId]);

  // The API sends the assigner as { userId, name, email }, so the raw object has no `id` and
  // every id-keyed helper below silently dropped them: absent from the mention list, unable to
  // be given a subtask, and missing from the admin escalation the backend does send them.
  const taskAssigner = useMemo(
    () => normalizeTaskUser(taskDetails?.assignedBy),
    [taskDetails?.assignedBy]
  );

  const adminRecipients = useMemo(
    () =>
      getTaskAdminRecipients({
        participants: [...allAssignees, taskAssigner].filter(Boolean),
        directory: orgUsers || [],
        excludeEmail: currentUserEmail,
      }),
    [allAssignees, taskAssigner, orgUsers, currentUserEmail]
  );
  const canNotifyAdmin = adminRecipients.length > 0;

  const subtaskAssignableUsers = useMemo(
    () =>
      getTaskAssignableUsers({
        people: [...allAssignees, taskAssigner].filter(Boolean),
        directory: orgUsers || [],
      }),
    [allAssignees, taskAssigner, orgUsers]
  );

  const subtasksHeldByPendingRemoval = useMemo(() => {
    if (!assigneePendingRemoval?.id) return 0;
    return getSubtasks(taskDetails).filter(
      (subtask) => getSubtaskAssignee(subtask)?.id === assigneePendingRemoval.id
    ).length;
  }, [taskDetails, assigneePendingRemoval?.id]);

  // Comments and change logs identify people by email; show their name where we know it.
  const nameByEmail = useMemo(() => {
    const map = new Map();
    const add = (person) => {
      if (person?.email && person?.name) map.set(person.email.toLowerCase(), person.name);
    };
    (usersList || []).forEach(add);
    allAssignees.forEach(add);
    add(taskAssigner);
    return map;
  }, [usersList, allAssignees, taskAssigner]);

  const displayPersonName = (email) =>
    (email && nameByEmail.get(String(email).toLowerCase())) || email || "Unknown";

  const mentionCandidates = useMemo(() => {
    const byId = new Map();
    const add = (person) => {
      if (person?.id != null && !byId.has(person.id)) byId.set(person.id, person);
    };
    allAssignees.forEach(add);
    add(taskAssigner);
    getSubtasks(taskDetails).forEach((subtask) => add(getSubtaskAssignee(subtask)));
    return orgUsers.filter((user) => byId.has(user.id));
  }, [allAssignees, taskAssigner, orgUsers, taskDetails]);

  const { mutate: changeStatus } = useChangeTaskStatus({
    onSuccess: () => toast.success("Status updated"),
    onError: (error) =>
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not update the status"
      ),
  });

  const { mutate: updateTask } = useUpdateTask({
    onSuccess: () => refetch(),
    onError: () => toast.error("Error updating task"),
  });

  const { mutate: reassignTask } = useUpdateTask({
    onSuccess: () => {
      toast.success("Task reassigned successfully");
      setIsReassignModalOpen(false);
      setReassignmentData({ assignTo: null, reasonOfReassignment: "" });
      refetch();
    },
    onError: () => toast.error("Error reassigning task"),
  });

  const { mutate: addAssignee } = useAddTaskAssignee({
    onSuccess: () => {
      toast.success("Assignee added");
      setNewAssignee(null);
      refetch();
    },
    onError: () => toast.error("Error adding assignee"),
  });

  const { mutate: removeAssignee, isLoading: isRemovingAssignee } = useRemoveTaskAssignee({
    onSuccess: () => {
      toast.success("Assignee removed");
      setAssigneePendingRemoval(null);
      refetch();
    },
    onError: () => {
      toast.error("Error removing assignee");
      setAssigneePendingRemoval(null);
    },
  });

  const { mutate: notifyAdmin, isLoading: isNotifyingAdmin } = useNotifyAdminAboutTask({
    onSuccess: () => {
      toast.success("Admins notified");
      setShowNotifyAdminDialog(false);
    },
    onError: () => toast.error("Error notifying admin"),
  });

  const { mutate: postComment } = usePostTaskComment({
    onSuccess: () => {
      setCommentText("");
      setCommentMentionIds([]);
      setCommentAttachments([]);
      refetchComments();
    },
    onError: () => toast.error("Error posting comment"),
  });

  const { mutate: deleteComment, isLoading: isDeletingComment } = useDeleteTaskComment({
    onSuccess: () => {
      setCommentPendingDeletion(null);
      refetchComments();
    },
    onError: () => {
      toast.error("Error deleting comment");
      setCommentPendingDeletion(null);
    },
  });

  const activityItems = useMemo(
    () => buildActivityItems(taskDetails?.changeLogs, comments),
    [taskDetails?.changeLogs, comments]
  );
  const activityEvents = useMemo(
    () => activityItems.filter((item) => item.kind !== "comment"),
    [activityItems]
  );
  const activityComments = useMemo(
    () => activityItems.filter((item) => item.kind === "comment"),
    [activityItems]
  );

  const additionalAssigneeIdsExcept = (...excludedIds) => {
    const excluded = new Set(excludedIds.filter((value) => value != null));
    return allAssignees
      .map((person) => person.id)
      .filter((assigneeId) => assigneeId != null && !excluded.has(assigneeId));
  };

  const quickUpdate = (changes) => {
    if (!taskDetails) return;
    updateTask({
      id,
      taskData: {
        clientId: taskDetails?.clientId,
        module: taskDetails?.module,
        specificTask: taskDetails?.specificTask,
        deadline: taskDetails?.deadline,
        comment: taskDetails?.comment,
        attachments: taskDetails?.attachments,
        priority: taskDetails?.priority,
        status: taskDetails?.status,
        assignTo: currentOwnerId,
        additionalAssigneeIds: additionalAssigneeIdsExcept(currentOwnerId),
        ...changes,
      },
    });
  };

  const handleReassign = () => {
    if (!reassignmentData.assignTo) {
      toast.error("Please select a new assignee");
      return;
    }
    if (!reassignmentData.reasonOfReassignment) {
      toast.error("Please provide a reason for reassignment");
      return;
    }
    const newOwnerId = reassignmentData.assignTo?.id;
    reassignTask({
      id,
      taskData: {
        clientId: taskDetails?.clientId,
        module: taskDetails?.module,
        specificTask: taskDetails?.specificTask,
        deadline: taskDetails?.deadline,
        comment: taskDetails?.comment,
        attachments: taskDetails?.attachments,
        priority: taskDetails?.priority,
        status: "PENDING",
        assignTo: newOwnerId,
        additionalAssigneeIds: additionalAssigneeIdsExcept(currentOwnerId, newOwnerId),
        reasonOfReassignment: reassignmentData.reasonOfReassignment,
      },
    });
  };

  const handleSubmitComment = () => {
    if (!commentText.trim() && commentAttachments.length === 0) return;
    const mentionIds = Array.from(
      new Set([...commentMentionIds, ...extractMentionIds(commentText, mentionCandidates)])
    );
    postComment({
      taskId: id,
      text: commentText.trim(),
      mentionedUserIds: mentionIds,
      attachments: commentAttachments,
    });
  };

  const handleSetOwner = () => {
    const ownerId = ownerDraft?.id;
    if (!ownerId) return;
    quickUpdate({
      assignTo: ownerId,
      additionalAssigneeIds: additionalAssigneeIdsExcept(currentOwnerId, ownerId),
    });
    setOwnerDraft(null);
  };

  const availableAssigneeOptions = orgUsers.filter((u) => !allAssignees.some((a) => a.id === u.id));

  if (isError) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-7xl items-center justify-center px-4 py-12 sm:px-6">
        <section className="w-full max-w-md rounded-xl border border-app-border bg-app-surface p-8 text-center shadow-sm">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600">
            <FaExclamationCircle className="h-6 w-6" />
          </span>
          <h2 className="text-lg font-semibold text-app-text">Task not found</h2>
          <p className="mt-2 text-sm leading-relaxed text-app-text-secondary">
            This task could not be found. It may have been deleted by the creator or may no longer
            exist.
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button variant="contained" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? "Retrying..." : "Try again"}
            </Button>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 p-4 md:p-6 shadow-lg rounded-xl bg-app-surface text-app-text">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <IconButton onClick={() => navigate(-1)} aria-label="Back">
              <IoArrowBack />
            </IconButton>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl md:text-2xl font-bold text-app-text truncate m-0">
                {taskDetails?.specificTask || "Task Details"}
              </h2>
              <p className="text-xs text-app-text-muted mt-0.5 mb-0 truncate">
                <ViewClientProfile
                  clientId={taskDetails?.clientId}
                  clientName={taskDetails?.clientName}
                  variant="link"
                />
                <span> · {taskDetails?.module || "—"}</span>
                {isModulePaymentTask && (
                  <span>
                    {" "}
                    · {getModulePaymentTaskTypeLabel(taskDetails?.taskType, taskDetails?.module)}
                  </span>
                )}
              </p>
            </div>
            {canEditDetails && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AiOutlineUserSwitch />}
                onClick={() => setIsReassignModalOpen(true)}
              >
                Reassign
              </Button>
            )}
            {canEditDetails && (
              <Button variant="contained" size="small" onClick={() => setIsEditTaskModalOpen(true)}>
                Edit
              </Button>
            )}
          </div>

          {isFetching && !taskDetails ? (
            <div className="flex justify-center items-center min-h-[320px]">
              <CircularProgress size={64} />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <Tooltip
                  title={
                    canChangeStatus
                      ? "Change status"
                      : "Only people on this task can change its status"
                  }
                >
                  <span>
                    <Chip
                      label={taskDetails?.status || "-"}
                      size="small"
                      className={`capitalize font-semibold ${getTaskStatusColor(
                        taskDetails?.status
                      )}`}
                      onClick={
                        canChangeStatus ? (e) => setStatusMenuAnchor(e.currentTarget) : undefined
                      }
                      onDelete={
                        canChangeStatus ? (e) => setStatusMenuAnchor(e.currentTarget) : undefined
                      }
                      deleteIcon={
                        canChangeStatus ? <span className="text-[10px] pr-1">▾</span> : undefined
                      }
                      sx={canChangeStatus ? undefined : { cursor: "default" }}
                    />
                  </span>
                </Tooltip>
                <Chip
                  label={taskDetails?.priority || "-"}
                  size="small"
                  className={`capitalize font-semibold ${getTaskPriorityColor(
                    taskDetails?.priority
                  )}`}
                  onClick={(e) => setPriorityMenuAnchor(e.currentTarget)}
                  onDelete={(e) => setPriorityMenuAnchor(e.currentTarget)}
                  deleteIcon={<span className="text-[10px] pr-1">▾</span>}
                />
                {taskDetails?.deadline && (
                  <Tooltip
                    title={`Due ${formatTaskDate(taskDetails.deadline)} · ${DISPLAY_DATE_TOOLTIP}`}
                  >
                    <Chip
                      size="small"
                      label={
                        daysLeftLabel
                          ? `${daysLeftLabel} · ${formatTaskDate(taskDetails.deadline)}`
                          : `Due ${formatTaskDate(taskDetails.deadline)}`
                      }
                      color={
                        deadlineInfo?.isPassed
                          ? "error"
                          : deadlineInfo?.isDueSoon
                          ? "warning"
                          : "default"
                      }
                      variant={deadlineInfo?.isPassed ? "filled" : "outlined"}
                    />
                  </Tooltip>
                )}
                {subtaskProgress.total > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${subtaskProgress.done}/${subtaskProgress.total} subtasks`}
                  />
                )}
                {comments.length > 0 && (
                  <Chip size="small" variant="outlined" label={`${comments.length} comments`} />
                )}
                <span className="flex-1" />
                <TaskAssigneeAvatars people={allAssignees} size={28} />
              </div>

              <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={() => setStatusMenuAnchor(null)}
              >
                {TASK_STATUS_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    selected={option.value === taskDetails?.status}
                    onClick={() => {
                      changeStatus({ id, status: option.value });
                      setStatusMenuAnchor(null);
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={priorityMenuAnchor}
                open={Boolean(priorityMenuAnchor)}
                onClose={() => setPriorityMenuAnchor(null)}
              >
                {TASK_PRIORITY_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    selected={option.value === taskDetails?.priority}
                    onClick={() => {
                      quickUpdate({ priority: option.value });
                      setPriorityMenuAnchor(null);
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Work flow */}
                <div className="lg:col-span-7 xl:col-span-8 space-y-5 min-w-0">
                  <div className="rounded-lg border border-app-border bg-app-surface-muted/50 px-3 py-2 mb-4">
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                      {priceListId && (
                        <DetailItem
                          label="Price list"
                          value={getPriceListTitle(priceListDetail, priceListId)}
                          onClick={() => setIsPriceListModalOpen(true)}
                        />
                      )}
                      {checkListId && (
                        <DetailItem
                          label="Check list"
                          value={getCheckListTitle(checkListDetail, checkListId)}
                          onClick={() => setIsCheckListModalOpen(true)}
                        />
                      )}
                      <DetailItem
                        label="Task created "
                        value={<DisplayDateTime value={taskDetails?.createdDate} />}
                      />
                      <DetailItem
                        label="Last updated"
                        value={<DisplayDateTime value={taskDetails?.updatedDate} />}
                      />
                    </div>
                  </div>
                  {taskDetails?.comment && (
                    <section className="rounded-xl border border-app-border p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                          1
                        </span>
                        <h3 className="text-sm font-semibold text-app-text m-0">Description</h3>
                      </div>

                      {taskDetails?.comment ? (
                        <p className="text-sm text-app-text whitespace-pre-wrap m-0 leading-relaxed">
                          {renderMentionSegments(taskDetails.comment).map((seg, idx) =>
                            seg.isMention ? (
                              <span key={idx} className="text-primary font-medium">
                                {seg.text}
                              </span>
                            ) : (
                              <span key={idx}>{seg.text}</span>
                            )
                          )}
                        </p>
                      ) : (
                        <p className="text-sm text-app-text-muted m-0">No description provided.</p>
                      )}
                    </section>
                  )}
                  <section className="rounded-xl border border-app-border p-4 md:p-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                          2
                        </span>
                        <h3 className="text-sm font-semibold text-app-text m-0">Subtasks</h3>
                      </div>
                      {subtaskProgress.total > 0 && (
                        <span className="text-xs text-app-text-muted">
                          {subtaskProgress.done} of {subtaskProgress.total} done
                        </span>
                      )}
                    </div>
                    <SubtaskManager
                      mode="live"
                      subtasks={taskDetails?.subtasks || []}
                      taskId={id}
                      onLiveChange={refetch}
                      users={subtaskAssignableUsers}
                      canManage={canEditDetails}
                      canAddSubtask={canChangeStatus}
                      currentUserEmail={currentUserEmail}
                      taskCreatedDate={taskDetails?.createdDate || taskDetails?.createdAt || null}
                    />
                  </section>
                  <section className="rounded-xl border border-app-border p-4 md:p-5 flex flex-col max-h-[calc(100vh-7rem)]">
                    <div className="flex items-center gap-2 mb-3 shrink-0">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                        3
                      </span>
                      <h3 className="text-sm font-semibold text-app-text m-0">
                        Discussion
                        {activityComments.length > 0 ? ` (${activityComments.length})` : ""}
                      </h3>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto space-y-3 mb-3 pr-1">
                      {activityComments.length === 0 ? (
                        <p className="text-sm text-app-text-muted text-center py-6 m-0">
                          No comments yet — start the discussion below.
                        </p>
                      ) : (
                        activityComments.map((item, idx) => {
                          // Your own comments sit on the right, everyone else's on the left.
                          const mine =
                            Boolean(item.postedBy) &&
                            String(item.postedBy).toLowerCase() ===
                              String(currentUserEmail || "").toLowerCase();
                          return (
                            <div
                              key={`c-${item.id ?? idx}`}
                              className={`group flex ${mine ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`flex max-w-[85%] flex-col ${
                                  mine ? "items-end" : "items-start"
                                }`}
                              >
                                <div
                                  className={`mb-1 flex items-center gap-2 ${
                                    mine ? "flex-row-reverse" : ""
                                  }`}
                                >
                                  <Tooltip title={item.postedBy || ""}>
                                    <span className="text-xs font-semibold text-app-text">
                                      {mine ? "You" : displayPersonName(item.postedBy)}
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={formatRelativeTime(item.date)}>
                                    <span className="text-xs text-app-text-muted whitespace-nowrap">
                                      {formatDisplayDateTime(item.date)}
                                    </span>
                                  </Tooltip>
                                  {mine && (
                                    <IconButton
                                      size="small"
                                      aria-label="Delete comment"
                                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                      onClick={() => setCommentPendingDeletion(item)}
                                    >
                                      <FaTrash className="text-xs text-app-text-secondary" />
                                    </IconButton>
                                  )}
                                </div>
                                {item.text && (
                                  <p
                                    className={`m-0 whitespace-pre-wrap rounded-2xl border px-3 py-2 text-sm ${
                                      mine
                                        ? "rounded-tr-md border-transparent bg-primary text-white"
                                        : "rounded-tl-md border-app-border-strong bg-app-surface-muted text-app-text"
                                    }`}
                                  >
                                    {renderMentionSegments(item.text).map((seg, i) =>
                                      seg.isMention ? (
                                        <span
                                          key={i}
                                          className={
                                            mine
                                              ? "font-semibold underline"
                                              : "text-primary font-medium"
                                          }
                                        >
                                          {seg.text}
                                        </span>
                                      ) : (
                                        <span key={i}>{seg.text}</span>
                                      )
                                    )}
                                  </p>
                                )}
                                {(item.attachments || []).length > 0 && (
                                  <div className="mt-1 w-full">
                                    <CommentAttachments attachments={item.attachments || []} />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="shrink-0">
                      <CommentComposer
                        text={commentText}
                        onTextChange={setCommentText}
                        mentionedUserIds={commentMentionIds}
                        onMentionedUserIdsChange={setCommentMentionIds}
                        users={mentionCandidates}
                        attachments={commentAttachments}
                        setAttachments={setCommentAttachments}
                        onSubmit={handleSubmitComment}
                      />
                    </div>
                  </section>
                  {taskDetails?.attachments?.length > 0 && (
                    <section className="rounded-xl border border-app-border p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                          4
                        </span>
                        <h3 className="text-sm font-semibold text-app-text m-0">Attachments</h3>
                      </div>
                      <TaskAttachmentList attachments={taskDetails.attachments} />
                    </section>
                  )}
                </div>

                {/* People + Timeline */}
                <aside className="lg:col-span-5 xl:col-span-4 space-y-4 min-w-0">
                  <div className="rounded-xl border border-app-border p-4 space-y-4">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-app-text-muted m-0">
                      People
                    </p>
                    <div>
                      <SidebarLabel>Assigned by</SidebarLabel>
                      <div className="flex items-center gap-2">
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                          {(taskDetails?.assignedBy?.name || "?").charAt(0).toUpperCase()}
                        </Avatar>
                        <span className="text-sm text-app-text">
                          {taskDetails?.assignedBy?.name || "—"}
                        </span>
                      </div>
                    </div>

                    <div>
                      <SidebarLabel>Primary Assignee</SidebarLabel>
                      {taskDetails?.assignedTo ? (
                        <div className="flex items-center gap-2">
                          <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                            {(taskDetails.assignedTo.name || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <span className="text-sm text-app-text flex-1 truncate">
                            {taskDetails.assignedTo.name || "—"}
                          </span>
                          {canEditDetails && (
                            <Tooltip title="Reassign">
                              <IconButton size="small" onClick={() => setIsReassignModalOpen(true)}>
                                <AiOutlineUserSwitch className="text-sm text-app-text-secondary" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      ) : canUpdateTask ? (
                        <div className="flex items-center gap-2">
                          <Autocomplete
                            size="small"
                            options={orgUsers}
                            getOptionLabel={(option) => option?.name || ""}
                            value={ownerDraft}
                            onChange={(_, value) => setOwnerDraft(value)}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Select assignee" />
                            )}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!ownerDraft}
                            onClick={handleSetOwner}
                          >
                            Set
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-app-text-muted m-0">Unassigned</p>
                      )}
                    </div>

                    <div>
                      <SidebarLabel>Collaborators</SidebarLabel>
                      {collaborators.length === 0 && (
                        <p className="text-sm text-app-text-muted m-0 mb-2">None</p>
                      )}
                      <ul className="space-y-2 m-0 p-0 list-none mb-2">
                        {collaborators.map((person) => (
                          <li
                            key={person.id ?? person.email}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Avatar sx={{ width: 24, height: 24, fontSize: 11 }}>
                                {(person.name || "?").charAt(0).toUpperCase()}
                              </Avatar>
                              <span className="text-sm text-app-text truncate">{person.name}</span>
                            </div>
                            {removableCollaboratorIds.has(person.id) && (
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() => setAssigneePendingRemoval(person)}
                                >
                                  <FaTrash className="text-xs text-app-text-secondary" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </li>
                        ))}
                      </ul>
                      {canManageCollaborators && (
                        <div className="flex items-center gap-2">
                          <Autocomplete
                            size="small"
                            options={availableAssigneeOptions}
                            getOptionLabel={(o) => o?.name || ""}
                            value={newAssignee}
                            onChange={(_, value) => setNewAssignee(value)}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Add person" />
                            )}
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!newAssignee}
                            onClick={() => addAssignee({ id, userId: newAssignee?.id })}
                          >
                            Add
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-app-border pt-3">
                      <Button
                        size="small"
                        color="warning"
                        fullWidth
                        disabled={!canNotifyAdmin}
                        onClick={() => setShowNotifyAdminDialog(true)}
                      >
                        Notify admin
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-app-border p-4 lg:sticky lg:top-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide font-semibold text-app-text-muted m-0">
                        Timeline
                        {activityEvents.length > 0 ? ` (${activityEvents.length})` : ""}
                      </p>
                      <Button size="small" onClick={() => setIsTimelineOpen((open) => !open)}>
                        {isTimelineOpen ? "Hide" : "Show"}
                      </Button>
                    </div>
                    {isTimelineOpen && (
                      <div className="mt-3 max-h-[min(520px,60vh)] overflow-y-auto pr-1">
                        {activityEvents.length === 0 ? (
                          <p className="text-sm text-app-text-muted m-0 py-6 text-center">
                            No status or assignment changes yet
                          </p>
                        ) : (
                          <ol className="relative m-0 p-0 list-none border-l border-app-border ml-2 space-y-4">
                            {activityEvents.map((item, idx) => (
                              <li key={`e-${idx}`} className="relative pl-4">
                                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-app-surface" />
                                <p className="text-sm text-app-text m-0 leading-snug">
                                  {defaultActivityMessage(item)}
                                </p>
                                <p className="text-xs text-app-text-muted m-0 mt-0.5">
                                  {formatRelativeTime(item.changedAt)}
                                </p>
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    )}
                  </div>
                </aside>
              </div>
            </>
          )}

          <Dialog
            open={isReassignModalOpen}
            onClose={() => setIsReassignModalOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Reassign Task</DialogTitle>
            <DialogContent>
              <div className="space-y-4 mt-4">
                <Autocomplete
                  options={orgUsers?.filter(
                    (user) => user.email !== taskDetails?.assignedTo?.email
                  )}
                  getOptionLabel={(option) => option.name}
                  value={reassignmentData.assignTo}
                  onChange={(_, newValue) =>
                    setReassignmentData((prev) => ({ ...prev, assignTo: newValue }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Select New Assignee" required fullWidth />
                  )}
                />
                <TextField
                  label="Reason for Reassignment"
                  multiline
                  rows={4}
                  value={reassignmentData.reasonOfReassignment}
                  onChange={(e) =>
                    setReassignmentData((prev) => ({
                      ...prev,
                      reasonOfReassignment: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsReassignModalOpen(false)}>Cancel</Button>
              <Button onClick={handleReassign} variant="contained" color="primary">
                Reassign
              </Button>
            </DialogActions>
          </Dialog>

          {isEditTaskModalOpen && (
            <TaskModal
              open={isEditTaskModalOpen}
              handleClose={() => setIsEditTaskModalOpen(false)}
              editingData={taskDetails}
              isEditing={true}
              refetchTaskDetails={refetch}
            />
          )}

          <ConfirmDialog
            open={assigneePendingRemoval != null}
            title="Remove assignee?"
            message={`${
              assigneePendingRemoval?.name || "This user"
            } will no longer be assigned to "${
              taskDetails?.specificTask || "this task"
            }" and loses access to it.${
              subtasksHeldByPendingRemoval
                ? ` ${subtasksHeldByPendingRemoval} subtask${
                    subtasksHeldByPendingRemoval === 1 ? "" : "s"
                  } assigned to them will be left unassigned.`
                : ""
            } You can add them back later.`}
            confirmLabel="Remove"
            confirmColor="error"
            loading={isRemovingAssignee}
            onConfirm={() => removeAssignee({ id, userId: assigneePendingRemoval?.id })}
            onCancel={() => setAssigneePendingRemoval(null)}
          />

          <ConfirmDialog
            open={commentPendingDeletion != null}
            title="Delete comment?"
            message="This comment will be removed from the task's activity for everyone. This cannot be undone."
            confirmLabel="Delete"
            confirmColor="error"
            loading={isDeletingComment}
            onConfirm={() => deleteComment({ taskId: id, commentId: commentPendingDeletion?.id })}
            onCancel={() => setCommentPendingDeletion(null)}
          />

          <NotifyAdminDialog
            open={showNotifyAdminDialog}
            onClose={() => setShowNotifyAdminDialog(false)}
            loading={isNotifyingAdmin}
            onSubmit={(message) => notifyAdmin({ id, message })}
          />

          {/* Read-only here — no onEdit, since these are managed from their own screens. */}
          <ViewPriceListModal
            open={isPriceListModalOpen}
            handleClose={() => setIsPriceListModalOpen(false)}
            data={priceListDetail}
          />

          <ViewCheckListModal
            open={isCheckListModalOpen}
            handleClose={() => setIsCheckListModalOpen(false)}
            data={checkListDetail}
          />
        </div>
      </div>
    </div>
  );
};

export default ViewTask;
